import AppRoutes from "./routes/AppRoutes";
import { useLanguage } from "./config/localization/LanguageProvider";
import { useEffect, useState } from "react";
import { FeedbackModal as ErrorModal } from "./components/ui/modals";
import {
  GameMaintenanceModal,
  SHOW_MAINTENANCE_EVENT,
  MaintenanceEventDetail,
} from "./components/ui/modals/GameMaintenanceModal";
import { useAppDispatch } from "./store/hooks";
import { fetchUserProfile } from "./features/users/actions/userProfileActions";
import { localStorageService } from "./services";
import { changeLoginModalState } from "./reducers/appStateReducer";
import { useSelector } from "react-redux";
import { FeedbackModal as LoginSuccessModal } from "./components/ui/modals/FeedbackModal";
import { deviceService } from "./utils/deviceService";
import userDeviceStore from "./store/device.store";
import { ToastProvider } from "./components/ui/toast/ToastProvider";
import localization from "./config/localization";
import { AccessMode } from "./models/constants/accessMode";
import { startSignalRConnection } from "./services/signalr.service";
import {
  registerSignalREvents,
  unregisterSignalREvents,
} from "./signalr/signalr.listener";

import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from "./components/ErrorFallback";

export default function App() {
  const [maintenance, setMaintenance] =
    useState<MaintenanceEventDetail | null>(null);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const showLoginSuccessModal = useSelector((state: any) => state.appState.showLoginSuccessModal);
  const dispatch = useAppDispatch();
  const showLoader = useSelector((state: any) => state.appState.showLoader);
  useLanguage();

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserProfile());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const isWrapperApp = navigator.userAgent.includes("Wrapper_App");

    userDeviceStore.setState({
      accessModeSource: {
        sourceType: isWrapperApp ? AccessMode.App : AccessMode.Web
      }
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const deviceType = deviceService.getDeviceType();
      userDeviceStore.setState({ deviceType });
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setIsAuthenticated(localStorageService.getItem("isUserLoggedIn") === true);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<MaintenanceEventDetail>;
      setMaintenance(customEvent.detail);
    };

    window.addEventListener(SHOW_MAINTENANCE_EVENT, handler);
    return () => {
      window.removeEventListener(SHOW_MAINTENANCE_EVENT, handler);
    };
  }, []);

  useEffect(() => {
    const titleElement = document.getElementById("title");
    if (titleElement) {
      titleElement.textContent = localization.site_title;
    }
  }, [localization.site_title]);


  useEffect(() => {
    if (!isAuthenticated) return;

    const token = localStorageService.getItem<string>("token");
    const userId = localStorageService.getItem<string>("userId");

    if (!token || !userId) return;

    let isActive = true;
    let currentConnection: any = null;

    const init = async () => {
      const conn = await startSignalRConnection(token);
      if (!isActive || !conn) return;

      currentConnection = conn;
      registerSignalREvents(conn, userId);
    };

    init();

    return () => {
      isActive = false;
      if (currentConnection) {
        unregisterSignalREvents(currentConnection);
      }
    };
  }, [isAuthenticated]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => {
      window.location.reload();
    }} >
      <ToastProvider maxToasts={3} maxAnnouncement={1}>
        <>
          <AppRoutes />

          <ErrorModal
            id="common-error-modal"
            isOpen={true}
            className="hidden"
            title="Failed!"
            icon="/icons/ic-error-modal.svg"
            onClose={() => {
              document.getElementById("common-error-modal")?.classList.add("hidden");
            }}
            closeButtonClassName="common-error-modal-close-button"
            blurColor="bg-[#FFD4D4]"
          />
          {/* Maintenance modal */}
          <GameMaintenanceModal
            data={maintenance}
            onClose={() => setMaintenance(null)}
          />

          <LoginSuccessModal
            isOpen={showLoginSuccessModal}
            title="Login Successful"
            icon="/icons/ic-success-modal.svg"
            blurColor="bg-[#D4FFDECC]"
            autoClose={true}
            onClose={() => dispatch(changeLoginModalState(false))}
          />
          {showLoader && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
            </div>
          )}
        </>
      </ToastProvider>
    </ErrorBoundary>
  );
}